import { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { handleFetchResponse } from 'api/errors'
import ModalForm from 'components/ModalForm/ModalForm'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import Seo from 'components/Seo'
import { required } from 'forms'
import { Form } from 'forms/Form'
import { useUserInfoQuery } from 'modules/login/api'
import IconInformation from 'remixicon/icons/System/information-line.svg?react'
import { changeFields } from 'shared/forms/mutators'

import { useCreateOrderYarcheMutation } from '../../api'
import MockOrderYarcheData from '../../components/MockOrderYarcheData'
import { ModalUserInfo, USER_INFO_NODE_ID } from '../../components/ModalUserInfo/ModalUserInfo'
import OrderCargoSize from '../../components/OrderCargoSize'
import { OrderCreateAgreeOffer } from '../../components/OrderCreate/OrderCreateAgreeOffer'
import OrderYarcheCreateDeparturePartner from '../../components/OrderYarcheCreateDeparturePartner'
import OrderYarcheCreateDestinationPartner from '../../components/OrderYarcheCreateDestinationPartner'
import OrderYarcheCreateSummary from '../../components/OrderYarcheCreateSummary'
import OrderYarcheGuestCreateFormRoute from '../../components/OrderYarcheGuestCreateFormRoute/OrderYarcheGuestCreateFormRoute'
import { orderDetailRoute, orderRoute } from '../../routes'
import { TOrder } from '../../types'
import { prepareOrderYarcheCrateData } from './helpers'
import { OrderYarcheCreateFormData } from './types'

const OrderYarcheCreatePage: FC<{
  initialValues?: Record<string, unknown>
  onFailure?: () => void
  onSuccess?: () => void
}> = ({
  initialValues = {
    cargo: {
      sizeId: 'envelope'
    }
  },
  onFailure,
  onSuccess
}) => {
  const isFirstRender = useRef(true)
  const [isCompleteUserName, setIsCompleteUserName] = useState(true)
  const [isCompleteUserDocument, setIsCompleteUserDocument] = useState(true)
  const [isShowUserInfoError, setIsShowUserInfoError] = useState(false)

  const navigate = useNavigate()
  const { closeModal, openModal } = useModal()

  const [createOrderYarche, { isLoading }] = useCreateOrderYarcheMutation()
  const {
    data: user,
    isLoading: isLoadingUserInfo,
    isFetching: isFetchingUserInfo,
    refetch: refetchUser
  } = useUserInfoQuery(undefined, { refetchOnMountOrArgChange: true })
  const isCompleteUser = Boolean(user?.firstname && user?.lastname)

  const handleClose = useCallback(() => {
    navigate(orderRoute)
    closeModal(MODAL_IDS.OrderYarcheCreate)
  }, [closeModal, navigate])

  const handleSuccess = useCallback(
    (response: { data: { order: TOrder } }) => {
      const { order } = response.data

      onSuccess?.()
      closeModal(MODAL_IDS.OrderYarcheCreate)
      navigate({ to: orderDetailRoute.to, params: { id: order.id } })
    },
    [closeModal, navigate, onSuccess]
  )

  const handleFailure = useCallback(() => {
    onFailure?.()
  }, [onFailure])

  const scrollToUserInfo = useCallback(() => {
    const containerNode = document.querySelector(`#${USER_INFO_NODE_ID}`)
    containerNode?.scrollIntoView()
  }, [])

  const handleSubmit = useCallback(
    async (data: OrderYarcheCreateFormData) => {
      const { data: userRefetch } = await refetchUser()
      const isCompleteUserName = Boolean(userRefetch?.firstname && userRefetch?.lastname)
      const isCompleteUserDocument = Boolean(userRefetch?.document?.length)
      const isCompleteUserRefetch = isCompleteUserName && isCompleteUserDocument

      if (isCompleteUserRefetch) {
        return createOrderYarche(prepareOrderYarcheCrateData(data)).then(
          handleFetchResponse(handleSuccess, handleFailure)
        )
      }

      setIsShowUserInfoError(true)
      setIsCompleteUserName(isCompleteUserName)
      setIsCompleteUserDocument(isCompleteUserDocument)
      setTimeout(scrollToUserInfo)
    },
    [createOrderYarche, handleFailure, handleSuccess, refetchUser, scrollToUserInfo]
  )

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      return
    }

    if (!(isLoadingUserInfo || isFetchingUserInfo) && !isCompleteUser) {
      setIsShowUserInfoError(true)
    }
  }, [closeModal, isCompleteUser, isFetchingUserInfo, isLoadingUserInfo])

  return (
    <>
      <Seo
        description="Создание новой посылки для размещения в сервисе доставки сборных грузов QP5 Express. Заполнение данных о грузе и предварительный расчет стоимости грузоперевозки."
        title="Новая посылка – сборные перевозки QP5 Express"
      />

      <ModalItem isOpen id={MODAL_IDS.OrderYarcheCreate}>
        <Form<OrderYarcheCreateFormData>
          keepDirtyOnReinitialize
          initialValues={initialValues}
          mutators={{ changeFields }}
          name="createOrderYarcheForm"
          onSubmit={handleSubmit}
          render={(formProps) => (
            <form name="createOrderYarcheForm" onSubmit={formProps.handleSubmit}>
              <ModalForm
                isStickyFooter
                isStickyHeader
                buttonLabel="Заказать"
                desktopClassName="max-w-[990px]"
                footerBefore={<OrderCreateAgreeOffer className="mb-4 md:mb-0" />}
                id={MODAL_IDS.OrderYarcheCreate}
                isLoading={isLoading}
                onClose={handleClose}
                size="xl"
                title="Оформление посылки"
              >
                <MockOrderYarcheData />
                <div className="grid grid-cols-1 gap-x-6 3md:grid-cols-[auto_356px]">
                  <div>
                    {isShowUserInfoError && (
                      <div className="mb-2">
                        <ModalUserInfo
                          isCompleteUserDocument={isCompleteUserDocument}
                          isCompleteUserName={isCompleteUserName}
                        />
                      </div>
                    )}

                    <div className="mb-4 grid grid-cols-[auto_1fr] items-center gap-x-4 rounded bg-attention-container p-3">
                      <IconInformation className="text-on-surface-variant" containerSize="nm" size="xs" />
                      <div className="text-body-md">
                        Сервис отправки и&nbsp;получения грузов через магазины &laquo;Ярче&raquo; работает
                        в&nbsp;тестовом режиме. Пока доступны только магазины Томска и&nbsp;Новосибирска.
                      </div>
                    </div>

                    <div className="pb-2 pt-4 text-headline-sm">Откуда</div>
                    <OrderYarcheGuestCreateFormRoute mode="departurePoint" validate={required} />
                    <div className="pb-2 pt-4 text-headline-sm">Куда</div>
                    <OrderYarcheGuestCreateFormRoute mode="destinationPoint" validate={required} />
                    <div className="pb-2 pt-4 text-headline-sm">Отправление</div>
                    <OrderCargoSize />
                    <div className="pb-2 pt-4 text-headline-sm">Отправитель</div>
                    <OrderYarcheCreateDeparturePartner />
                    <div className="pb-2 pt-4 text-headline-sm">Получатель</div>
                    <OrderYarcheCreateDestinationPartner />
                  </div>
                  <div className="py-4 pl-0 md:pl-6">
                    <OrderYarcheCreateSummary className="sticky top-[90px]" />
                  </div>
                  <MockOrderYarcheData excludes={['departurePartner']} />
                </div>
              </ModalForm>
            </form>
          )}
        />
      </ModalItem>
    </>
  )
}

export default memo(OrderYarcheCreatePage)
